import { createAuthedThunk } from '../authedThunk';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { auth } from '../../firebase';
import axios from 'utils/axios';

const initialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  errorText: null,
  summaries: [],
  // Org-wide (cross deal) summaries used by the Reports page. Kept separate
  // from `summaries` so the deal page tab and the Reports page don't clobber
  // each other.
  fetchingAll: null,
  fetchingAllSuccessful: null,
  fetchingAllFailed: null,
  allErrorText: null,
  allSummaries: [],
  // The v2 listing scoped to one deal, used by the risk report's per deal sub
  // page. Kept apart from `allSummaries` so opening a deal doesn't clobber the
  // Reports list rendered behind it.
  fetchingDeal: null,
  fetchingDealSuccessful: null,
  fetchingDealFailed: null,
  dealErrorText: null,
  dealSummaries: [],
};

export const fetchDealSummaries = createAsyncThunk(
  'dealSummary/fetchDealSummaries',
  async ({ dealId }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          try {
            const token = await user?.getIdToken();
            const response = await axios.get(
              `/us/qai/deals/${dealId}/deal-summaries`,
              {
                params: { 'request-id': uuidv4() },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              },
            );
            if (response.status === 200) {
              resolve(response.data);
            } else {
              reject(rejectWithValue('Failed to get deal summaries'));
            }
          } catch (e) {
            reject(rejectWithValue(e?.response?.data || e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

/**
 * Fetches deal summaries across every deal in the caller's org (v2 endpoint).
 * Pass a `dealId` to scope it to a single deal. Each summary carries a
 * `dealInfo` object so no per-deal lookup is needed to render a list.
 *
 * `type` narrows to one record kind (`PERIOD_SUMMARY` / `RISK_REPORT` /
 * `EMAILS_NEEDING_ATTENTION` / `DEALS_CLOSING_THIS_WEEK`) and `sortByRiskScore`
 * orders a risk report's own `customFields.riskyDeals` entries (not the
 * records) by `riskScore` descending, which only means anything paired with
 * `type=RISK_REPORT`. The Reports page fetches every type in one go, filters
 * client side and sorts the risky deals itself, so both are only used by
 * callers that want a single kind.
 */
export const fetchAllDealSummaries = createAsyncThunk(
  'dealSummary/fetchAllDealSummaries',
  async (payload, { rejectWithValue }) => {
    const dealId = payload?.dealId;
    const type = payload?.type;
    const sortByRiskScore = payload?.sortByRiskScore;
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          try {
            const token = await user?.getIdToken();
            const response = await axios.get(
              `/us/qai/v2/deals/deal-summaries`,
              {
                params: {
                  'request-id': uuidv4(),
                  ...(dealId ? { dealId } : {}),
                  ...(type ? { type } : {}),
                  ...(sortByRiskScore ? { sortByRiskScore: true } : {}),
                },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              },
            );
            if (response.status === 200) {
              resolve(response.data);
            } else {
              reject(rejectWithValue('Failed to get deal summaries'));
            }
          } catch (e) {
            reject(rejectWithValue(e?.response?.data || e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

/**
 * Same v2 listing as `fetchAllDealSummaries`, scoped to one deal.
 *
 * A per-deal call matches on `dealIds` (array-contains) and *trims* the
 * multi-deal record types down to the deal that was asked for: a `RISK_REPORT`
 * comes back carrying only that deal's `riskyDeals` entry. That is exactly
 * what the per deal risk sub page renders, so it reads the freshest record
 * rather than the copy sitting in the org-wide listing.
 */
export const fetchDealSummariesV2 = createAuthedThunk(
  'dealSummary/fetchDealSummariesV2',
  async (token, payload) => {
    const dealId = payload?.dealId;
    const type = payload?.type;
    const response = await axios.get(`/us/qai/v2/deals/deal-summaries`, {
      params: {
        'request-id': uuidv4(),
        ...(dealId ? { dealId } : {}),
        ...(type ? { type } : {}),
      },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 200) return response.data;
    throw new Error('Failed to get deal summaries');
  },
  // The reports page mounts several cards that ask for the same summary set.
  {
    dedupeKey: (payload) =>
      `dealSummariesV2:${payload?.dealId || 'all'}:${payload?.type || 'any'}`,
  },
);

const dealSummarySlice = createSlice({
  name: 'dealSummary',
  initialState,
  reducers: {
    resetDealSummaries: (state) => {
      state.fetching = null;
      state.fetchingSuccessful = null;
      state.fetchingFailed = null;
      state.errorText = null;
      state.summaries = [];
    },
    resetDealSummariesV2: (state) => {
      state.fetchingDeal = null;
      state.fetchingDealSuccessful = null;
      state.fetchingDealFailed = null;
      state.dealErrorText = null;
      state.dealSummaries = [];
    },
    resetAllDealSummaries: (state) => {
      state.fetchingAll = null;
      state.fetchingAllSuccessful = null;
      state.fetchingAllFailed = null;
      state.allErrorText = null;
      state.allSummaries = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDealSummaries.pending, (state) => {
        state.fetching = true;
        state.fetchingSuccessful = false;
        state.fetchingFailed = false;
        state.errorText = null;
      })
      .addCase(fetchDealSummaries.fulfilled, (state, action) => {
        state.fetching = false;
        state.fetchingSuccessful = true;
        state.fetchingFailed = false;
        state.summaries = action.payload || [];
      })
      .addCase(fetchDealSummaries.rejected, (state, action) => {
        state.fetching = false;
        state.fetchingSuccessful = false;
        state.fetchingFailed = true;
        state.errorText = action.payload || 'Failed to get deal summaries';
        state.summaries = [];
      })
      .addCase(fetchAllDealSummaries.pending, (state) => {
        state.fetchingAll = true;
        state.fetchingAllSuccessful = false;
        state.fetchingAllFailed = false;
        state.allErrorText = null;
      })
      .addCase(fetchAllDealSummaries.fulfilled, (state, action) => {
        state.fetchingAll = false;
        state.fetchingAllSuccessful = true;
        state.fetchingAllFailed = false;
        state.allSummaries = action.payload || [];
      })
      .addCase(fetchAllDealSummaries.rejected, (state, action) => {
        state.fetchingAll = false;
        state.fetchingAllSuccessful = false;
        state.fetchingAllFailed = true;
        state.allErrorText = action.payload || 'Failed to get deal summaries';
        state.allSummaries = [];
      })
      .addCase(fetchDealSummariesV2.pending, (state) => {
        state.fetchingDeal = true;
        state.fetchingDealSuccessful = false;
        state.fetchingDealFailed = false;
        state.dealErrorText = null;
      })
      .addCase(fetchDealSummariesV2.fulfilled, (state, action) => {
        state.fetchingDeal = false;
        state.fetchingDealSuccessful = true;
        state.fetchingDealFailed = false;
        state.dealSummaries = action.payload || [];
      })
      .addCase(fetchDealSummariesV2.rejected, (state, action) => {
        state.fetchingDeal = false;
        state.fetchingDealSuccessful = false;
        state.fetchingDealFailed = true;
        state.dealErrorText = action.payload || 'Failed to get deal summaries';
        state.dealSummaries = [];
      });
  },
});

export const {
  resetDealSummaries,
  resetAllDealSummaries,
  resetDealSummariesV2,
} = dealSummarySlice.actions;

export default dealSummarySlice.reducer;
